import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { writeToClipboard } from "../lib/writeToClipboard";
import { AnimatedCopyIcon } from "./AnimatedCopyIcon";
import { EmptyState } from "./EmptyState";
import { PressButton } from "./PressButton";
import { StatusIndicator } from "./StatusIndicator";
import { Text } from "./Text";

type ErrorPageAction = { label: string; onPress: () => void };

const Actions = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;
`;

const ActionButton = styled(PressButton)`
  padding: 10px 18px;
  border: none;
  border-radius: 9999px;
  color: var(--text-primary);
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  font-family: inherit;

  &:hover {
    background: var(--surface-hover);
  }
  &:active {
    opacity: 0.8;
  }
`;

export function ErrorPage({
  getCopyText,
  primaryAction,
  supportAction,
}: {
  getCopyText: () => string;
  primaryAction: ErrorPageAction;
  supportAction: ErrorPageAction;
}) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <EmptyState
      icon={<StatusIndicator state="error" focused size={24} />}
      title="Something went wrong"
      description="An unexpected error interrupted the app. Reload, or contact support if it persists."
      action={
        <Actions>
          <ActionButton onPress={primaryAction.onPress}>
            <Text variant="text-regular-md">{primaryAction.label}</Text>
          </ActionButton>
          <ActionButton onPress={supportAction.onPress}>
            <Text variant="text-regular-md">{supportAction.label}</Text>
          </ActionButton>
          <ActionButton
            onPress={() => {
              void writeToClipboard(getCopyText()).then((ok) => {
                if (!ok) return;
                setCopied(true);
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            <AnimatedCopyIcon copied={copied} />
            <Text variant="text-regular-md">
              {copied ? "Copied" : "Copy error details"}
            </Text>
          </ActionButton>
        </Actions>
      }
    />
  );
}
